import React from 'react'
import PropTypes from 'prop-types'
import { Field, Form, Formik } from 'formik'
import { FormattedMessage, useIntl } from 'react-intl'
import { GROUP_TITLE_TEXT_MAX } from 'app/constants/group'
import SimpleInput from 'app/componentV4/Input/SimpleInput'
import SubGroupDynamicTextInput from './SubGroupDynamicTextInput'
import ContainedButton from 'app/componentV4/Button/ContainedButton'
import { CLOSE, SECONDARY } from 'app/constants/button'
import LabelInput from 'app/componentV4/Labels/LabelInput'

const CreateGroupForm = ({
  onSubmit,
  onCancel,
  disabled = false,
  containerClassName = '',
  submitButtonLabelId = 'action.create',
  id,
}) => {
  const { formatMessage } = useIntl()

  const validateCreateGroupForm = (values) => {
    const errors = {}

    const subGroupErrors = []
    let hasAtLeastOneFilled = false
    const seenTitles = new Set()

    if (!values.groupName) {
      errors.groupName = formatMessage({
        id: 'composeDialog.field.required',
      })
    }

    if (values.groupName.length > GROUP_TITLE_TEXT_MAX) {
      errors.groupName = formatMessage(
        {
          id: 'maximum.characters.allowed',
        },
        { limit: GROUP_TITLE_TEXT_MAX },
      )
    }

    values.subGroups?.forEach((group, index) => {
      const trimmed = (group?.title || '').trim()

      if (trimmed !== '') {
        hasAtLeastOneFilled = true

        if (trimmed.length > 50) {
          subGroupErrors[index] = {
            title: formatMessage(
              { id: 'maximum.characters.allowed' },
              { limit: 50 },
            ),
          }
        }

        if (seenTitles.has(trimmed)) {
          subGroupErrors[index] = {
            title: formatMessage({
              id: 'groupv4.add.subGroup.duplicate.error.text',
            }),
          }
        } else {
          seenTitles.add(trimmed)
        }
      }
    })

    if (!hasAtLeastOneFilled) {
      subGroupErrors[0] = {
        title: formatMessage({ id: 'composeDialog.field.required' }),
      }
    }

    if (subGroupErrors.length > 0) {
      errors.subGroups = subGroupErrors
    }

    return errors
  }

  return (
    <Formik
      enableReinitialize={true}
      validateOnChange={true}
      validateOnBlur={true}
      initialValues={{
        groupName: '',
        subGroups: [{ title: '' }],
      }}
      validate={validateCreateGroupForm}
      onSubmit={onSubmit}
    >
      {({
        dirty,
        isValid,
        isSubmitting,
        setFieldValue,
        setFieldTouched,
        errors,
      }) => {
        return (
          <Form className={containerClassName || 'px-5 py-5'}>
            <div className="mb-5 w-full">
              <Field name="groupName">
                {({ field }) => (
                  <LabelInput
                    label={formatMessage({
                      id: 'group.table.th.groupName',
                    })}
                    required
                    className="w-full"
                  >
                    <SimpleInput
                      {...field}
                      id={id ? `${id}-groupNameInput` : 'group-name'}
                      type="text"
                      disabled={disabled}
                      className="h-48 w-full mb-1 sm:mb-0 bg-white rounded-8 text-blue-950"
                      placeholder={formatMessage({
                        id: 'groupsv4.add.dialog.groupName.placeholder',
                      })}
                      onBlur={(e) => {
                        setFieldTouched('groupName', true)
                      }}
                      error={Boolean(errors.groupName)}
                      helperText={errors.groupName}
                    />
                  </LabelInput>
                )}
              </Field>
            </div>

            <div className="mb-5 w-full">
              <div className="text-14 text-primary-300 p-4 bg-primary-200">
                <FormattedMessage id="groupsv4.add.dialog.warning.message" />
              </div>
            </div>

            <div className="mb-10 w-full">
              <SubGroupDynamicTextInput
                name="subGroups"
                disabled={disabled}
                id={
                  id
                    ? `${id}-subGroupInput`
                    : 'create-group-form-sub-group-input'
                }
                onChange={(newValues) => {
                  setFieldValue('subGroups', newValues)
                  setFieldTouched('subGroups', true)
                }}
              />
            </div>

            <div className="flex justify-center space-x-5 mt-5">
              <ContainedButton
                id={id ? `${id}-closeBtn` : 'close-create-group-btn'}
                color={CLOSE}
                type="button"
                disabled={disabled || isSubmitting}
                onClick={onCancel}
                className="normal-case h-40"
              >
                <FormattedMessage id="text.close" />
              </ContainedButton>
              <ContainedButton
                id={id ? `${id}-submitBtn` : 'create-group-btn'}
                color={SECONDARY}
                type="submit"
                isLoading={disabled || isSubmitting}
                disabled={!dirty || !isValid || isSubmitting || disabled}
                className="normal-case h-40"
              >
                <FormattedMessage id={submitButtonLabelId} />
              </ContainedButton>
            </div>
          </Form>
        )
      }}
    </Formik>
  )
}

CreateGroupForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  containerClassName: PropTypes.string,
  submitButtonLabelId: PropTypes.string,
  id: PropTypes.string,
}

export default CreateGroupForm
